/**
 * Tokenizing JavaScript for the builder's code fields.
 *
 * Not a parser — a colourer. It splits source into runs (comment, string,
 * number, keyword, call, punctuation, plain) that the editor paints under a
 * transparent textarea. The one property everything depends on: concatenating
 * the tokens gives back the source byte-for-byte, or the paint drifts out from
 * under the caret.
 */

export type TokenKind = "keyword" | "string" | "number" | "comment" | "call" | "punct" | "plain";

export interface Token {
  text: string;
  kind: TokenKind;
}

const KEYWORDS = new Set([
  "async",
  "await",
  "break",
  "case",
  "catch",
  "class",
  "const",
  "continue",
  "default",
  "delete",
  "do",
  "else",
  "export",
  "extends",
  "false",
  "finally",
  "for",
  "function",
  "if",
  "import",
  "in",
  "instanceof",
  "let",
  "new",
  "null",
  "of",
  "return",
  "super",
  "switch",
  "this",
  "throw",
  "true",
  "try",
  "typeof",
  "undefined",
  "var",
  "void",
  "while",
  "with",
  "yield",
]);

/** Comments and strings tolerate a missing closer — mid-keystroke, most of
 *  them are missing one, and the colour flickering off would read as breakage. */
const PATTERN =
  /(\/\/[^\n]*|\/\*[\s\S]*?(?:\*\/|$))|("(?:[^"\\\n]|\\.)*(?:"|$)|'(?:[^'\\\n]|\\.)*(?:'|$)|`(?:[^`\\]|\\[\s\S])*(?:`|$))|(\b\d(?:[\w]|\.\d)*)|([A-Za-z_$][\w$]*)|([^\sA-Za-z_$\d]+)/g;

export function tokenize(source: string): Token[] {
  const tokens: Token[] = [];
  let at = 0;
  PATTERN.lastIndex = 0;
  for (let match = PATTERN.exec(source); match; match = PATTERN.exec(source)) {
    if (match.index > at) tokens.push({ text: source.slice(at, match.index), kind: "plain" });
    const [text, comment, string, number, word] = match;
    const kind: TokenKind = comment
      ? "comment"
      : string
        ? "string"
        : number
          ? "number"
          : word
            ? KEYWORDS.has(word)
              ? "keyword"
              : /^\s*\(/.test(source.slice(match.index + text.length))
                ? "call"
                : "plain"
            : "punct";
    tokens.push({ text, kind });
    at = match.index + text.length;
  }
  if (at < source.length) tokens.push({ text: source.slice(at), kind: "plain" });
  return tokens;
}
